import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Clock, DollarSign, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";

const CATS = ["All", "Industrial", "Technical", "Clerical", "Construction"];

export default function Jobs() {
    const [params, setParams] = useSearchParams();
    const initCat = params.get("category") || "All";
    const [category, setCategory] = useState(initCat);
    const [q, setQ] = useState("");
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const p = {};
        if (category !== "All") p.category = category;
        if (q.trim()) p.q = q.trim();
        api.get("/jobs", { params: p })
            .then((r) => setJobs(r.data))
            .finally(() => setLoading(false));
    }, [category, q]);

    return (
        <PageShell>
            <section className="bg-brand-navy text-white py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">Job Board</div>
                    <h1 className="font-heading text-4xl md:text-6xl font-black uppercase">Open positions</h1>
                    <p className="mt-3 text-slate-300 max-w-2xl">Find your next role across industrial, technical, and clerical placements in the Fort Wayne area.</p>
                </div>
            </section>

            <section className="bg-white border-b-2 border-slate-100 sticky top-20 z-10" data-testid="job-filters">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-3">
                    <div className="flex flex-wrap gap-2">
                        {CATS.map((c) => (
                            <button
                                key={c}
                                onClick={() => {
                                    setCategory(c);
                                    const np = new URLSearchParams(params);
                                    if (c === "All") np.delete("category"); else np.set("category", c);
                                    setParams(np);
                                }}
                                className={`px-4 py-2 font-heading uppercase tracking-wider text-xs font-bold border-2 rounded-sm transition-colors ${
                                    category === c
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-brand-navy border-slate-200 hover:border-brand-orange"
                                }`}
                                data-testid={`filter-cat-${c.toLowerCase()}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto relative flex-1 max-w-sm min-w-[200px]">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title or keyword..."
                            className="w-full pl-9 pr-3 py-2 border-2 border-slate-200 rounded-sm focus:outline-none focus:border-brand-orange text-sm"
                            data-testid="job-search-input"
                        />
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
                {loading ? (
                    <div className="text-slate-500">Loading positions...</div>
                ) : jobs.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="font-heading text-2xl font-bold text-brand-navy">No openings match your filters.</div>
                        <p className="mt-2 text-slate-500">Try clearing your search or selecting a different category — or apply now and we'll match you when one opens.</p>
                        <Link to="/apply" className="mt-6 inline-block bg-brand-orange text-white font-heading uppercase font-bold tracking-wider px-6 py-3 rounded-sm">Apply Now</Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-grid">
                        {jobs.map((j) => (
                            <Link key={j.id} to={`/jobs/${j.id}`} className="group border-2 border-slate-200 hover:border-brand-orange bg-white p-6 block transition-colors" data-testid={`job-list-item-${j.id}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-orange">{j.category}</div>
                                        <h3 className="font-heading text-2xl font-black uppercase text-brand-navy mt-2 group-hover:text-brand-orange transition-colors">{j.title}</h3>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" />
                                </div>
                                <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
                                    <span className="flex gap-1"><MapPin className="h-4 w-4" />{j.location}</span>
                                    <span className="flex gap-1"><Clock className="h-4 w-4" />{j.shift}</span>
                                    <span className="flex gap-1 font-bold text-brand-navy"><DollarSign className="h-4 w-4" />{j.pay_rate}</span>
                                </div>
                                <div className="mt-2 text-xs uppercase tracking-wider text-slate-500 font-bold">{j.employment_type}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </PageShell>
    );
}
